import { ofType, combineEpics } from "redux-observable";
import { from, Observable, of, timer } from "rxjs";
import {
  map,
  switchMap,
  mergeMap,
  delay,
  repeat,
  concatMap,
  startWith,
  takeUntil
} from "rxjs/operators";
import { responseFrom, ResponseAction, request } from "../../ajax";
import { Action } from "../../Store";
import { Actions } from "./state/actions";
import { State } from "./state/state";

// These epic patterns described by react-observable maintainer here:
// https://stackoverflow.com/questions/48261438/composing-and-sequencing-multiple-epics-in-redux-observable/48307271#48307271
// This allows to run through ajax reducers.
// TODO - replace the mergeMap pipe with a pipable operator so we can reuse this pattern
export const getEpic = (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    ofType(Actions.REQUEST_GET),
    mergeMap(() =>
      action$.pipe(
        responseFrom("/ATX/alerts"),
        mergeMap((a: ResponseAction<State.ServerResponse>) =>
          of(Actions.getResponse({ data: a.data }))
        ),
        startWith(request("/ATX/alerts"))
      )
    )
  );

/**
 * @brief This uses concatMap to throttle the incoming alerts. The first
 * concatMap throttles building up of alerts from multiple GET requests.
 * The second concatMap wait's for ALERT_CLR before sending the next
 * ALERT_SET.
 *
 * TODO - Canceling alert will flush out the inner observable, but the outer
 * observable seems to keep going...
 */
export const setAlertEpic = (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    ofType(Actions.RESPONSE_GET),
    // Throttle incoming alerts.  Do not advertise next batch of alerts until
    // previous alerts are finished.
    switchMap((a: Actions.GetResponse) =>
      from(a.data.alerts.recent.reverse().slice(0, a.diff)).pipe(
        // Throttle here because we do not set alert until previous alert
        // has cleared
        concatMap(a =>
          // This timer never expires, but it keeps concatMap throttled until
          // the ALERT_CLR signal
          timer(1, 10000).pipe(
            mergeMap(() => of(Actions.alertSet({ data: a })).pipe(delay(200))),
            takeUntil(action$.pipe(ofType(Actions.ALERT_CLR)))
          )
        ),
        takeUntil(action$.pipe(ofType(Actions.ALERT_CANCEL)))
      )
    )
  );

export const clrAlertEpic = (action$: Observable<Action>) =>
  action$.pipe(
    ofType(Actions.ALERT_SET),
    concatMap(() =>
      of(Actions.alertClr()).pipe(
        delay(6000),
        // User canceled notification so cancel our clear notification
        takeUntil(action$.pipe(ofType(Actions.ALERT_CLR)))
      )
    )
  );

export const cancelAlertEpic = (action$: Observable<Action>) =>
  action$.pipe(
    ofType(Actions.ALERT_CANCEL),
    mergeMap(() => from([Actions.alertClr()]))
  );

export const pollEpic = (action$: Observable<Action>) =>
  action$.pipe(
    ofType(Actions.POLL_START),
    switchMap(() =>
      action$.pipe(
        ofType(Actions.RESPONSE_GET),
        delay(5000),
        map(() => Actions.getRequest()),
        repeat(),
        startWith(Actions.getRequest()),
        takeUntil(action$.pipe(ofType(Actions.POLL_STOP)))
      )
    )
  );

export default combineEpics(
  getEpic,
  setAlertEpic,
  clrAlertEpic,
  cancelAlertEpic,
  pollEpic
);
